import {Injectable, inject, signal} from '@angular/core';
import {OctopusConnectService, DataEntity} from 'octopus-connect';
import {Observable, throwError} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import {
    applyDeliverableModerationStatus,
    filterLearnerDeliverableSaves,
} from '../models/iackathon-deliverable-moderation';
import {IackathonSaveAttributes, IackathonSaveContent} from '../models/iackathon-save.attributes';
import {IackathonDeliverableModerationStatus} from '../models/iackathon-save.state';

type RawIackathonSaveAttributes = Omit<IackathonSaveAttributes, 'content'> & { content: string };
const LEARNER_DELIVERABLE_PAGE_SIZE = 50;

export type IackathonSave = {
    id: string;
    attributes: IackathonSaveAttributes;
    originalEntity?: DataEntity<RawIackathonSaveAttributes>;
};
export type IackathonInvalidSave = {
    kind: 'invalid';
    id: string;
    ownerUsername?: string;
    errorKey: 'iackathon.save_format_error';
    originalEntity?: DataEntity<RawIackathonSaveAttributes>;
};
export type IackathonSaveListItem = IackathonSave | IackathonInvalidSave;

export function isIackathonInvalidSave(save: IackathonSaveListItem): save is IackathonInvalidSave {
    return 'kind' in save && save.kind === 'invalid';
}

export function isIackathonSave(save: IackathonSaveListItem): save is IackathonSave {
    return !isIackathonInvalidSave(save);
}

@Injectable({
    providedIn: 'root',
})
export class IackathonSavesService {
    private octopusConnect = inject(OctopusConnectService);

    public readonly trainerSaveItems = signal<IackathonSaveListItem[]>([]);
    public readonly trainerSaves = signal<IackathonSave[]>([]);
    public readonly learnerDeliverables = signal<IackathonSave[]>([]);
    public readonly cachedSaves = this.trainerSaves;

    public fetchSaves(): Observable<IackathonSaveListItem[]> {
        return this.fetchTrainerSaveItems();
    }

    public fetchTrainerSaves(): Observable<IackathonSave[]> {
        return this.fetchTrainerSaveItems().pipe(
            map(saveItems => saveItems.filter(isIackathonSave)),
        );
    }

    private fetchTrainerSaveItems(): Observable<IackathonSaveListItem[]> {
        return this.octopusConnect.loadCollection<RawIackathonSaveAttributes>('iackathon', {
            showLearners: 'true'
        }).pipe(
            map(saveCollection => saveCollection.entities.map(entity => this.mapRawEntitySafely(entity))),
            tap(saveItems => {
                this.trainerSaveItems.set(saveItems);
                this.trainerSaves.set(saveItems.filter(isIackathonSave));
            }),
        );
    }

    public fetchLearnerAccessibleDeliverables(): Observable<IackathonSave[]> {
        const paginatedSaves = this.octopusConnect.paginatedLoadCollection<RawIackathonSaveAttributes>('iackathon', {
            filter: {
                moderationStatus: 'approved'
            },
            page: 1,
            range: LEARNER_DELIVERABLE_PAGE_SIZE,
        });

        return new Observable<IackathonSave[]>(subscriber => {
            const learnerSaves: IackathonSave[] = [];
            const subscription = paginatedSaves.collectionObservable.subscribe({
                next: saveCollection => {
                    learnerSaves.push(
                        ...saveCollection.entities.map(entity => this.mapRawEntitySafely(entity)).filter(isIackathonSave)
                    );

                    if (paginatedSaves.paginator.hasNextPage) {
                        paginatedSaves.paginator.page = paginatedSaves.paginator.page + 1;
                        return;
                    }

                    const learnerDeliverables = filterLearnerDeliverableSaves(learnerSaves);
                    this.learnerDeliverables.set(learnerDeliverables);
                    subscriber.next(learnerDeliverables);
                    subscriber.complete();
                },
                error: error => subscriber.error(error),
            });

            return () => subscription.unsubscribe();
        });
    }

    public updateDeliverableModerationStatus(
        save: IackathonSave,
        status: IackathonDeliverableModerationStatus,
        _approvedAt?: number
    ): Observable<IackathonSave> {
        if (!save.originalEntity) {
            return throwError(() => new Error('Cannot moderate an iackathon save without its raw entity'));
        }

        const nextAttributes = applyDeliverableModerationStatus(save.attributes, status);

        save.originalEntity.set('moderationStatus', nextAttributes.moderationStatus);

        return save.originalEntity.save().pipe(
            map(entity => this.mapRawEntity(entity as DataEntity<RawIackathonSaveAttributes>)),
            tap(updatedSave => this.replaceCachedSave(updatedSave)),
        );
    }

    public replaceCachedSave(updatedSave: IackathonSave): void {
        this.trainerSaves.update(saves => replaceSave(saves, updatedSave));
        this.trainerSaveItems.update(saves => replaceSave(saves, updatedSave));
        this.learnerDeliverables.update(saves => filterLearnerDeliverableSaves(replaceSave(saves, updatedSave)));
    }

    private mapRawEntitySafely(entity: DataEntity<RawIackathonSaveAttributes>): IackathonSaveListItem {
        try {
            return this.mapRawEntity(entity);
        } catch (error) {
            const invalidSave = this.mapInvalidEntity(entity);
            this.captureInvalidSave(error, invalidSave);

            return invalidSave;
        }
    }

    private mapRawEntity(entity: DataEntity<RawIackathonSaveAttributes>): IackathonSave {
        const content = JSON.parse(entity.attributes.content);
        assertValidIackathonSaveContent(content);

        return {
            id: entity.id.toString(),
            attributes: {
                ...entity.attributes,
                content
            },
            originalEntity: entity,
        };
    }

    private mapInvalidEntity(entity: DataEntity<RawIackathonSaveAttributes>): IackathonInvalidSave {
        return {
            kind: 'invalid',
            id: entity.id.toString(),
            ownerUsername: entity.attributes.owner?.username,
            errorKey: 'iackathon.save_format_error',
            originalEntity: entity,
        };
    }

    private captureInvalidSave(error: unknown, invalidSave: IackathonInvalidSave): void {
        console.error('[iackathon] Invalid save content', {
            saveId: invalidSave.id,
            ownerUsername: invalidSave.ownerUsername,
        }, error);

        Sentry.captureException(error, {
            tags: {
                feature: 'iackathon',
                saveId: invalidSave.id,
            },
            extra: {
                saveId: invalidSave.id,
                ownerUsername: invalidSave.ownerUsername,
            },
        });
    }
}

function replaceSave<T extends IackathonSaveListItem>(saves: T[], updatedSave: IackathonSave): T[] {
    const existingSaveIndex = saves.findIndex(save => save.id === updatedSave.id);

    if (existingSaveIndex === -1) {
        return saves;
    }

    return [
        ...saves.slice(0, existingSaveIndex),
        updatedSave,
        ...saves.slice(existingSaveIndex + 1),
    ] as T[];
}

function assertValidIackathonSaveContent(content: unknown): asserts content is IackathonSaveContent {
    if (!isRecord(content)) {
        throw new Error('Invalid iackathon save content: content must be an object');
    }

    if (!isRecord(content.state)) {
        throw new Error('Invalid iackathon save content: state must be an object');
    }

    if (content.state.group_name && !Array.isArray(content.state.members)) {
        throw new Error('Invalid iackathon save content: members must be an array');
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
